import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Users, Truck, Leaf, Recycle } from "lucide-react";
import { SCOPE_TOTAL_POPULATION } from "@/config/scope";
import useSWR from "swr";
import { fetcher } from "@/lib/api";

/**
 * WOW KPI band — big numbers, big icons, count-up on viewport enter.
 * Reads real values from /api/public/impacto/summary and /recogida.
 * Honours prefers-reduced-motion.
 */

function AnimatedNumber({ to, decimals = 0, duration = 1600 }) {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState(reduced ? to : 0);
  const mv = useMotionValue(reduced ? to : 0);
  const spring = useSpring(mv, { damping: 40, stiffness: 90 });
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });

  useEffect(() => {
    if (inView) mv.set(to);
  }, [inView, to, mv]);

  useEffect(() => {
    return spring.on("change", (v) => {
      const val = decimals > 0 ? Number(v).toFixed(decimals) : Math.round(v);
      setDisplay(
        new Intl.NumberFormat("es-ES", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }).format(Number(val))
      );
    });
  }, [spring, decimals]);

  return (
    <span ref={ref} className="tabular-nums">
      {display}
    </span>
  );
}

function KpiTile({ icon: Icon, value, unit, label, sub, testid, iconColor, decimals, note }) {
  const hasValue = value != null && !Number.isNaN(value);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-3xl bg-white border border-salnes-stone/10 p-8 lg:p-10 shadow-sm hover:shadow-xl transition-shadow duration-300"
      data-testid={testid}
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
        style={{ background: `${iconColor}18`, color: iconColor }}
        aria-hidden
      >
        <Icon size={28} strokeWidth={1.75} />
      </div>
      <div className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-none text-salnes-ink">
        {hasValue ? (
          <AnimatedNumber to={value} decimals={decimals || 0} />
        ) : (
          "—"
        )}
        {unit && hasValue && (
          <span className="text-lg sm:text-xl font-sans text-salnes-stone ml-2 align-top mt-2 inline-block tracking-normal">
            {unit}
          </span>
        )}
      </div>
      <div className="mt-4 text-salnes-ink font-medium text-base sm:text-lg">
        {label}
      </div>
      {sub && (
        <div className="mt-1 text-salnes-stone text-xs sm:text-sm">{sub}</div>
      )}
      {note && (
        <div
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-salnes-sand/60 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-salnes-stone"
          title={note}
          data-testid={`${testid}-estimation-badge`}
        >
          <span aria-hidden>◐</span>
          {note}
        </div>
      )}
    </motion.div>
  );
}

export default function KpisWow() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data: carbon } = useSWR("/public/impacto/carbon-by-concello", fetcher, {
    revalidateOnFocus: false,
    refreshInterval: 15 * 60 * 1000,
  });
  const { data: sum } = useSWR("/public/impacto/summary", fetcher, {
    revalidateOnFocus: false,
    refreshInterval: 15 * 60 * 1000,
  });

  const carbonTotals = carbon?.totals || {};
  const kgTotal = carbonTotals.kg_total ?? null;
  const tTotal = kgTotal != null ? Math.round(kgTotal / 1000) : null;
  const co2eKg = carbonTotals.co2e_kg ?? null;
  const detecciones = sum?.kpis?.detecciones_procesadas ?? null;
  const estimationMethod = carbon?.estimation_method || "real";
  const isEstimated = estimationMethod !== "real";
  const estimationNote =
    isEstimated
      ? lang === "es"
        ? "Estimación conservadora · mín. plausible (MITECO)"
        : "Estimación conservadora · mín. plausible (MITECO)"
      : null;

  return (
    <section
      id="kpis-wow"
      className="relative bg-gradient-to-b from-salnes-ivory to-white py-24 lg:py-32"
      data-testid="landing-kpis-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15% 0px" }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mb-14"
        >
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-4">
            {lang === "es" ? "Impacto en directo" : "Impacto en directo"}
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-salnes-ink">
            {lang === "es"
              ? "Los números del Salnés, honestos y actualizados"
              : "Os números do Salnés, honestos e actualizados"}
          </h2>
          <p className="mt-4 text-salnes-stone text-lg max-w-xl leading-relaxed">
            {lang === "es"
              ? "Datos reales sincronizados desde el sistema operativo de recogida. No inventamos nada."
              : "Datos reais sincronizados desde o sistema operativo de recollida. Non inventamos nada."}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          <KpiTile
            icon={Users}
            value={SCOPE_TOTAL_POPULATION}
            unit="hab."
            label={lang === "es" ? "Personas atendidas" : "Persoas atendidas"}
            sub={lang === "es" ? "3 municipios del Salnés" : "3 concellos do Salnés"}
            testid="kpi-wow-population"
            iconColor="#0B4C7A"
          />
          <KpiTile
            icon={Recycle}
            value={tTotal}
            unit="t"
            label={lang === "es" ? "Residuos recogidos" : "Residuos recollidos"}
            sub={lang === "es" ? "Este año (YTD)" : "Este ano (YTD)"}
            testid="kpi-wow-recogidos"
            iconColor="#10B981"
          />
          <KpiTile
            icon={Leaf}
            value={co2eKg != null ? Math.round(co2eKg / 1000) : null}
            unit="t CO₂e"
            label={lang === "es" ? "Huella estimada" : "Pegada estimada"}
            sub={lang === "es" ? "Factor MITECO" : "Factor MITECO"}
            testid="kpi-wow-co2"
            iconColor="#E76F51"
            note={estimationNote}
          />
          <KpiTile
            icon={Truck}
            value={detecciones}
            label={lang === "es" ? "Detecciones IA" : "Deteccións IA"}
            sub={lang === "es" ? "Incidencias registradas" : "Incidencias rexistradas"}
            testid="kpi-wow-containers"
            iconColor="#6B7280"
          />
        </div>

        <p className="mt-10 text-xs text-salnes-stone italic">
          {lang === "es"
            ? "Los indicadores marcados con “—” aún no reciben datos suficientes. Cuando la fuente correspondiente publique la métrica, se activarán automáticamente."
            : "Os indicadores marcados con “—” aínda non reciben datos suficientes. Cando a fonte correspondente publique a métrica, activaranse automaticamente."}
        </p>
      </div>
    </section>
  );
}
